/**
 * Ke mana seorang pengguna mendarat setelah masuk — peta §5.1.
 *
 * "Sign in → verifikasi → muat entitlement → arahkan ke beranda SESUAI PERAN
 * (OPR/LDR ke aplikasi lapangan, MGR/AUD ke `/dashboard`, KON ke `/portfolio`,
 * PLT ke `/platform/tenants`)."
 *
 * Yang benar-benar terjadi sebelum ini: SEMUA orang mendarat di `/dashboard`.
 * Akibatnya dua defect UAT yang terlihat berbeda tetapi berpangkal sama:
 *
 *   · D-14 — Yanto (OPR) menerima Dasbor MANAJER lengkap dengan "Antrean
 *     keputusan" dan "Susun laporan bulanan", bilah sisi KOSONG, dan satu-satunya
 *     kendali yang berfungsi adalah "Keluar". Tidak ada kebocoran data (RLS tetap
 *     membatasi barisnya ke areanya); yang bocor adalah MAKSUD layarnya.
 *   · D-10 — konsultan dan admin platform mendarat di Dasbor, yang memanggil
 *     `GET /v1/home`, yang menjawab `403` dengan BENAR karena aktor PQM tidak
 *     terikat pada satu tenant — dan konsol menampilkan "Kami tidak dapat memuat
 *     ini" atas jawaban yang persis seperti yang diharapkan.
 *
 * Keputusannya diangkat menjadi fungsi murni supaya ia dapat diuji, dan supaya
 * peta §5.1 punya satu tempat yang dapat dibaca berdampingan dengannya.
 *
 * `LDR` mendarat di `/dashboard`, bukan di aplikasi lapangan, dan itu penyimpangan
 * yang DISENGAJA dari huruf peta §5.1: §5.2 memberi Daftar Temuan izin LDR dan
 * §5.1 sendiri memberi Dasbor izin "LDR MGR", jadi konsol memang miliknya juga.
 * Yang peta maksud adalah bahwa pekerjaan LAPANGAN seorang leader ada di ponsel.
 */
export type LandingKind =
  | { kind: 'route'; route: string }
  /** Perannya tidak punya layar konsol: katakan itu, jangan render layar orang lain. */
  | { kind: 'field-app' }
  /** Layarnya memang ada di peta, tetapi belum dibangun di gelombang ini. */
  | { kind: 'not-built'; route: string };

export interface LandingActor {
  actorKind: string;
  roleCode: string | null;
}

/**
 * Rute konsol yang BENAR-BENAR dibangun di gelombang ini.
 *
 * `/privacy-notice` dan `/accept-invitation` TIDAK ada di daftar ini, dan itu bukan
 * kelalaian: keduanya dilayani di atas cabang `anonymous` di App.tsx, sebelum
 * router yang memakai daftar ini pernah berjalan. Menambahkannya ke sini akan
 * menyarankan bahwa keduanya rute di dalam kerangka bersesi, dan keduanya bukan.
 */
export const BUILT_ROUTES = [
  // OPERATIONS
  '/dashboard',
  '/findings',
  '/findings/new',
  '/verification-queue',
  '/audits',
  '/meetings/daily',
  '/meetings/escalation',
  '/portfolio',
  // INSIGHT
  '/reports',
  '/search',
  // MANAGE
  '/locations',
  '/users',
  '/points',
  // PLATFORM
  '/platform/tenants',
  '/platform/memberships',
  '/platform/modules',
  '/platform/audit',
  '/platform/health',
  // Tanpa slot navigasi — peta §4
  '/settings/account',
  /**
   * `/settings/account/privacy` dan BUKAN `/settings/privacy`. Perancang menggabungkan
   * Pengaturan Privasi menjadi BAGIAN dari Pengaturan Akun (`188:1225`): breadcrumb ke
   * induknya, kerangka sidebar identik, nol butir navigasi baru. Alamat lamanya tetap
   * bekerja lewat tabel PENGALIHAN di `App.tsx` — ia dinormalkan sebelum sampai ke sini,
   * jadi ia tidak perlu dan tidak boleh ada di daftar ini: sebuah pengalihan yang juga
   * terdaftar sebagai rute terbangun adalah rute yang gerbang inventaris layar akan
   * tuntut acuan Figma-nya, untuk sesuatu yang tidak pernah digambar siapa pun.
   */
  '/settings/account/privacy',
  '/settings/organisation',
] as const;

/**
 * Ke mana setiap peran mendarat.
 *
 * SATU ATURAN, dan `navigasi.test.ts` menegakkannya untuk semua peran sekaligus:
 * **rute pendaratan wajib ada di navigasi peran itu.** Sebuah pendaratan di layar
 * yang tidak ada di bilah sisi adalah layar yang tidak dapat dicapai kembali, dan
 * — ketika izin rute datanya juga berbeda — layar yang menampilkan galat atas
 * jawaban `403` yang benar (UAT D-10).
 *
 * Dua koreksi dari bentuk sebelumnya, keduanya ditemukan dengan membaca
 * `GET /v1/me/nav` persona demi persona:
 *
 *   · `AUD` → `/audits`, bukan `/dashboard`. Butir nav Dasbor berizin `LDR MGR`.
 *   · `FAC` → `/meetings/daily`, bukan `/dashboard`. Selain tidak ada di navigasinya,
 *     `GET /v1/home` berizin `OPR LDR MGR AUD` — tanpa FAC — jadi Dasbor menjawab
 *     `403` untuk fasilitator.
 *
 * `OPR` tetap `field-app`: peta §5.1 menempatkan pekerjaan operator di ponsel, dan
 * konsol tidak berpura-pura menjadi ponsel. Yang BERUBAH adalah bahwa layar itu
 * sekarang bukan jalan buntu — operator punya dua butir navigasi sungguhan
 * (`/findings/new` dan `/findings`) dan `FieldAppOnly` menautkan keduanya.
 */
export function landingFor(actor: LandingActor): LandingKind {
  if (actor.actorKind === 'PLATFORM') return { kind: 'route', route: '/platform/tenants' };
  if (actor.actorKind === 'CONSULTANT') return { kind: 'route', route: '/portfolio' };

  switch (actor.roleCode) {
    case 'MGR':
    case 'LDR':
      return { kind: 'route', route: '/dashboard' };
    case 'AUD':
      return { kind: 'route', route: '/audits' };
    case 'FAC':
      return { kind: 'route', route: '/meetings/daily' };
    case 'OPR':
      return { kind: 'field-app' };
    default:
      // Peran yang tidak dikenal konsol tidak mendapat layar orang lain.
      return { kind: 'field-app' };
  }
}

/**
 * Apakah rute ini punya layar yang sudah dibangun.
 *
 * Dipakai router untuk memilih antara layar sungguhan dan `ScreenNotBuiltState` —
 * BUKAN `ModuleNotInPlanState`, yang menjawab pertanyaan yang sama sekali lain
 * (UAT D-07).
 */
export function isBuiltRoute(path: string): boolean {
  if (path === '' || path === '/') return true;
  if ((BUILT_ROUTES as readonly string[]).includes(path)) return true;
  // Rute detail. `/findings/new` sudah tertangkap daftar di atas, jadi pola ini
  // tidak pernah membacanya sebagai kode temuan.
  if (/^\/findings\/[^/]+$/.test(path)) return true;
  if (/^\/users\/[^/]+$/.test(path)) return true;
  // `/locations/:id` TIDAK diakui: layar Location Detail tidak ada di peta §5 dan
  // tidak ada di router. Mengakuinya membuat rute itu jatuh ke `else` terakhir
  // `App.tsx`, yang merender Dasbor — sehingga mengklik satu baris area mengembalikan
  // manajer ke layar yang baru saja ia klik, tanpa satu pun pesan.
  if (/^\/platform\/tenants\/[^/]+$/.test(path)) return true;
  return false;
}
